import { useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { getPortalData } from '@bynder/portal-and-user-data';
import { API } from '../api';
import { AgentCapability } from '../../types';
import { AGENT_CAPABILITY_FEATURE_MAP } from './constants';

interface UseAgentCapabilitiesResult {
	capabilities: AgentCapability[];
	isLoading: boolean;
	error: Error | null;
}

/**
 * Hook to fetch and manage agent capabilities based on portal features
 */
export function useAgentCapabilities(): UseAgentCapabilitiesResult {
	const [capabilities, setCapabilities] = useState<AgentCapability[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		setIsLoading(true);
		setError(null);

		getPortalData()
			.then((portalData) => {
				if (!portalData) {
					throw new Error('Failed to get portal data');
				}

				return API.getPortalFeatures(portalData.id);
			})
			.then((features) => {
				const enabledCapabilities: AgentCapability[] = [];

				for (const [capability, featureId] of Object.entries(
					AGENT_CAPABILITY_FEATURE_MAP,
				)) {
					if (features.includes(featureId)) {
						enabledCapabilities.push(capability as AgentCapability);
					}
				}

				setCapabilities(enabledCapabilities);
				setIsLoading(false);
			})
			.catch((err) => {
				const fetchError =
					err instanceof Error ? err : new Error('Failed to fetch agent capabilities');
				setError(fetchError);
				setCapabilities([]);
				setIsLoading(false);
				captureException(err);
			});
	}, []);

	return { capabilities, isLoading, error };
}

/**
 * Helper to check if a specific capability is enabled
 */
export function hasCapability(
	capabilities: AgentCapability[],
	capability: AgentCapability,
): boolean {
	return capabilities.includes(capability);
}

/**
 * Helper to check if any of the provided capabilities are enabled
 */
export function hasAnyCapability(
	capabilities: AgentCapability[],
	desiredCapabilities: AgentCapability[],
): boolean {
	return desiredCapabilities.some((capability) => capabilities.includes(capability));
}
